var _typeof = typeof Symbol === "function" && typeof Symbol.iterator === "symbol" ? function (obj) { return typeof obj; } : function (obj) { return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; };

var _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; };

var _slicedToArray = function () { function sliceIterator(arr, i) { var _arr = []; var _n = true; var _d = false; var _e = undefined; try { for (var _i = arr[Symbol.iterator](), _s; !(_n = (_s = _i.next()).done); _n = true) { _arr.push(_s.value); if (i && _arr.length === i) break; } } catch (err) { _d = true; _e = err; } finally { try { if (!_n && _i["return"]) _i["return"](); } finally { if (_d) throw _e; } } return _arr; } return function (arr, i) { if (Array.isArray(arr)) { return arr; } else if (Symbol.iterator in Object(arr)) { return sliceIterator(arr, i); } else { throw new TypeError("Invalid attempt to destructure non-iterable instance"); } }; }();

import camelCase from 'lodash/camelCase';
import flow from 'lodash/flow';
import fromPairs from 'lodash/fromPairs';
import isArray from 'lodash/isArray';
import pick from 'lodash/pick';
import omit from 'lodash/omit';
import isEmpty from 'lodash/isEmpty';
import isObject from 'lodash/isObject';
import isPlainObject from 'lodash/isPlainObject';
import map from 'lodash/map';
import mapKeys from 'lodash/mapKeys';
import mapValues from 'lodash/mapValues';
import snakeCase from 'lodash/snakeCase';
import toPairs from 'lodash/toPairs';
import { normalize } from 'normalizr';
import { camelToSnake, snakeToCamel } from '../string';

/**
 * @param {string} key Key to fetch
 * @param {object} fieldsNameMap Object to fetch key from
 */
var _getKey = function _getKey(key, fieldsNameMap) {
    var newKey = key;

    if (fieldsNameMap[key]) {
        newKey = fieldsNameMap[key];
    }

    return snakeToCamel(newKey);
};

/**
 * Get a value from a given set of fields
 * @param {*} value
 * @param {*} fields
 * @param {*} fieldsNameMap
 * @param {boolean} isWhiteList
 */
var _getValue = function _getValue(value, fields, fieldsNameMap, isWhiteList) {
    var newValue = value;

    if (isObject(value) && !isArray(value)) {
        /*eslint-disable no-use-before-define*/
        newValue = _nestedObjectHelper(value, fields, fieldsNameMap, isWhiteList);
    } else if (isArray(value)) {
        newValue = value.map(function (v) {
            return _getValue(v, fields, fieldsNameMap, isWhiteList);
        });
    }

    return newValue;
};

/**
 * Pick or omit keys from an object
 * @param {object} obj
 * @param {string[]} fields
 * @param {boolean} isWhiteList
 */
var _selectKeysFromResponseObject = function _selectKeysFromResponseObject(obj, fields, isWhiteList) {
    if (isEmpty(fields)) {
        return obj;
    }

    var method = isWhiteList ? 'pick' : 'omit';
    var methods = { pick: pick, omit: omit };
    var prunedObj = methods[method](obj, fields);

    return prunedObj;
};

/**
* Recursively traverses the passed in object turning each key to camelCase and will
* exclude/include any key located in the fields
* @param {object} obj
* @param {string[]} fields
* @param {object} fieldsNameMap
* @param {boolean} isWhiteList
*/
var _nestedObjectHelper = function _nestedObjectHelper(obj, fields, fieldsNameMap, isWhiteList) {
    var prunedObj = _selectKeysFromResponseObject(obj, fields, isWhiteList);

    var fn = flow(toPairs, map, function (kvps) {
        return kvps.map(function (_ref) {
            var _ref2 = _slicedToArray(_ref, 2),
                key = _ref2[0],
                val = _ref2[1];

            return [_getKey(key, fieldsNameMap), _getValue(val, fields, fieldsNameMap, isWhiteList)];
        });
    }, fromPairs);

    return fn(prunedObj);
};

/**
 * Determines if a key should bge removed
 * @param {boolean} isWhiteList
 * @param {string} key
 * @param {object[]} fields
 */
var _shouldRemoveKey = function _shouldRemoveKey(isWhiteList, key, fields) {
    var isKeyInFields = fields.indexOf(key) > -1;

    return isWhiteList && !isKeyInFields || !isWhiteList && isKeyInFields;
};

/**
* This function is a default behavior that defaults the assignEntity process for the
* main normalize function call.
*
* The key processes it allows are:
*     1. transition of snake_case to camelCase for variables at any depth in the response object.
*     2. removal/inclusion of any keys listed in fields at any depth in the response object.
*
*
* If you do not want this function to be called at all simply send null as the parameter value.
*
* If you need extra functionality beyond what this provides you are welcome to write your own, examples on
* the API and usage cases can be found at the normalizr repo: https://github.com/paularmstrong/normalizr
*
* @param {object} obj The transformed response value being built as input is being stepped over by the normalize function
* @param {string} key The current key of the input object being iterated over
* @param {any} val The value of the current key at the input object
* @param {object[]} fields check param of transformUtil
* @param {object} fieldsNameMap check param of transformUtil
* @param {object} isWhiteList check param of transformUtil
*/
var _defaultFunc = function _defaultFunc(obj, key, val, fields, fieldsNameMap, isWhiteList) {

    /* Because of the way normalizr handles arrays it is necessary to do a key by key check here */
    if (!isEmpty(fields) && _shouldRemoveKey(isWhiteList, key, fields)) {
        return;
    }

    /* This is required to match the functionality of the assignEntity function provided by normalizr */
    /*eslint-disable no-param-reassign*/
    obj[_getKey(key, fieldsNameMap)] = _getValue(val, fields, fieldsNameMap, isWhiteList);
};

/**
* A transformUtil for receiving responses from the backend that modifies the objects into
* maps by ID, and many other useful features as detailed on the normalizr repo (link).
*
* This wrapper is provided that adds the functionality change all keys from snake_case to camelCase
* and remove any unwanted fields from the response.
*
* The only required fields are the response and the schema object, which is created in the standard
* normalizr fasion. Supports overwriting the default assignEntity function if you wish extra
* custom behavior.
* @param {object} options
* @param {object} options.response jSON Object which is the API response
* @param {object} options.schema The normalizr Schema object that you would like to apply to the response object
* @param {object} [options.options] The options block that will be passed to the normalize call. Will be merged with default options so any overwriting is possible.
* @param {object[]} [options.fields] Array of key values to either white list or black list from the normalized object
*                 IMPORTANT: For whitelisted parameters you must include the entire path to the key
*                 you want to keep. The normalize function operates one depth at a time
* @param {object} [options.fieldsNameMap] Object in where the `key` will be renamed with the name of the `value`
* @param {boolean} [options.isWhiteList] - Boolean determining whether to whitelist or blacklist the keys in `fields` param.
*/
export var transformUtil = function transformUtil(_ref3) {
    var response = _ref3.response,
        schema = _ref3.schema,
        _ref3$options = _ref3.options,
        options = _ref3$options === undefined ? {} : _ref3$options,
        _ref3$fields = _ref3.fields,
        fields = _ref3$fields === undefined ? [] : _ref3$fields,
        _ref3$fieldsNameMap = _ref3.fieldsNameMap,
        fieldsNameMap = _ref3$fieldsNameMap === undefined ? {} : _ref3$fieldsNameMap,
        _ref3$isWhiteList = _ref3.isWhiteList,
        isWhiteList = _ref3$isWhiteList === undefined ? false : _ref3$isWhiteList;

    var normalizedResponse = void 0;

    if (schema) {
        var defaultOptions = {
            assignEntity: function assignEntity(obj, key, val) {
                _defaultFunc(obj, key, val, fields, fieldsNameMap, isWhiteList);
            }
        };

        normalizedResponse = normalize(response, schema, _extends({}, defaultOptions, options));
    }

    return normalizedResponse;
};

/**
 * A simple util to denormalize entities and ids into a single array
 * @param {string[]} ids The list of ids that serve as keys for entities
 * @param {object} entities Map of entity objects
 * @returns A denormalized array of entities containing the given ids
 */
export var denormalize = function denormalize(ids, entities) {
    return ids.map(function (id) {
        return entities[id];
    });
};

/**
 * Invokes a function against every object or item in an array
 * @param {object | object[]} obj
 * @param {(...args) => any} mapper
 */
export var deep = function deep(obj, mapper) {
    if (isArray(obj)) {
        return obj.map(function (value) {
            return deep(value, mapper);
        });
    }

    if (isPlainObject(obj)) {
        return mapValues(mapper(obj), function (value) {
            return deep(value, mapper);
        });
    }

    return obj;
};

var keysToCamel = function keysToCamel(obj) {
    return mapKeys(obj, function (value, key) {
        return camelCase(key);
    });
};
var keysToSnake = function keysToSnake(obj) {
    return mapKeys(obj, function (value, key) {
        return snakeCase(key);
    });
};

/**
 * Deeply converts all the object or array of object's keys to camelCase
 * @param {*} obj Object with keys to convert
 */
export var deepKeysToCamel = function deepKeysToCamel(obj) {
    return deep(obj, keysToCamel);
};

/**
 * Deeply converts all the object or array of object's keys to snake_case
 * @param {*} obj Object with keys to convert
 */
export var deepKeysToSnake = function deepKeysToSnake(obj) {
    return deep(obj, keysToSnake);
};

/**
 * Recursively transform nested objects into a single flat object with combined
 * keys. {root: {nested: 'value'}} becomes {rootNested: 'value'}
 */
export var flattenObject = function flattenObject(obj) {
    var object = obj;

    /* eslint-disable guard-for-in */
    for (var key in object) {
        var value = object[key];

        if ((typeof value === 'undefined' ? 'undefined' : _typeof(value)) !== 'object' || value === null || !object.hasOwnProperty(key)) {
            continue;
        }

        // for multi-level flattening
        value = flattenObject(value);

        for (var nestedKey in value) {
            if (!value.hasOwnProperty(nestedKey)) {
                continue;
            }

            object[snakeToCamel(key + '_' + nestedKey)] = value[nestedKey];
        }

        delete object[key];
    }
    return object;
};
/**
 * uses our specific camelToSnake instead of _.snakeCase
 * this is because lodash makes event.destination => event_destination
 */
export var keysCamelToSnake = function keysCamelToSnake(obj) {
    return mapKeys(obj, function (value, key) {
        return camelToSnake(key);
    });
};